import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import {
  CheckMark3Icon,
  PrintIcon,
  StarFilledIcon,
  UserFourIcon,
  UserSevenIcon,
  WarningIcon
} from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Modal from '@repo/ui/modal'
import TextLink from '@repo/ui/text-link'
import Typography from '@repo/ui/typography'
import UserRoleCard from '@repo/ui/user-role-card'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'

import { API, constructApiUrl } from '../../../api/apis.config'
import { resolveCenterEvent } from '../../../api/center-event/resolve-center-event'
import { getCenterFacTerrain } from '../../../api/centers/get-center-fac-terrain'
import { getCenterManager } from '../../../api/centers/get-center-manager'
import { Paths } from '../../../lib/constants/path'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { useSecureFile } from '../../../lib/hooks/use-secure-file'
import { eventStatusEnumMapping } from '../../../lib/types/enums/center-event/event-status.enum'
import { eventTypeEnumMapping } from '../../../lib/types/enums/center-event/event-type.enum'
import { affectedStaffMainFields } from '../../../lib/types/fields/affected-staffs/affected-staff'
import { CenterEvent } from '../../../lib/types/models/center-event/center-event'
import { CenterEventMode } from '../../../lib/types/models/center-event/center-event-input'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'

type Props = {
  centerEvent: CenterEvent
  mode?: CenterEventMode
}

const CenterEventHeader = ({ centerEvent, mode }: Props) => {
  const queryClient = useQueryClient()
  const { toggleModal, modalState, closeModel } = useModalManager(['resolve'])
  const centerId = centerEvent.centerId
  // Links only on the event sheet itself: inside the modal on the child's own
  // profile they would just point to the page already open.
  const studentProfilePath =
    mode === 'NORMAL' && centerEvent.student
      ? Paths.ENROLLMENT_PROFILE_PAGE.replace(':id', centerEvent.student.id.toString())
      : undefined
  const studentFullName = centerEvent.student
    ? toFullName(centerEvent.student.childFirstName, centerEvent.student.childLastName, {
        options: { mode: 'lastname-firstname' }
      })
    : ''
  const studentPictureClassName =
    'flex justify-center items-center rounded-xl bg-white-dark w-14 h-14'
  const { downloadDocuments } = useDownloadDocuments()
  const {
    url,
    isLoading: isPicLoading,
    status: picStatus
  } = useSecureFile(centerEvent.student?.secureIdentityPicture?.uploadId)
  const isPicError = picStatus === 'INFECTED' || picStatus === 'FAILED'
  const studentPicture = isPicLoading ? (
    <Loader size="xs" />
  ) : isPicError ? (
    <WarningIcon color={tailwindColors.secondaryRed.DEFAULT} />
  ) : (
    <img src={url ?? '/profile-default.jpg'} className="rounded-lg w-12 h-12 object-cover" />
  )

  const { mutateAsync, isPending: isResolvingCenterEventPending } = useMutation({
    mutationFn: () => resolveCenterEvent(centerEvent.id),
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('L’événement a été clôturé avec succès')
      queryClient.invalidateQueries({ queryKey: ['center-events'] })
      closeModel('resolve')
    }
  })

  const { mutate: print, isPending: isPrintPending } = useMutation({
    mutationFn: () =>
      downloadDocuments({
        endpoint: constructApiUrl(API.centerEvents.printPdf.url, { id: centerEvent.id })
      }),
    onError: showAxiosError
  })

  const { data: centerManager } = useQuery({
    queryKey: ['center-manager', centerId, affectedStaffMainFields],
    queryFn: () => getCenterManager(centerId, affectedStaffMainFields)
  })

  const { data: facTerrain } = useQuery({
    queryKey: ['centers', centerId, 'fac-terrain'],
    queryFn: () => getCenterFacTerrain(centerId)
  })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2 items-center">
          {centerEvent.student && (
            <div className="flex gap-4 items-center">
              {studentProfilePath ? (
                <Link to={studentProfilePath} className={studentPictureClassName}>
                  {studentPicture}
                </Link>
              ) : (
                <div className={studentPictureClassName}>{studentPicture}</div>
              )}
              <div className="flex gap-2 items-center">
                <Typography variant="h6">
                  {studentProfilePath ? (
                    <TextLink
                      href={studentProfilePath}
                      name={studentFullName}
                      options={{ variant: 'no-underline' }}
                    />
                  ) : (
                    studentFullName
                  )}
                </Typography>
                {centerEvent.student.isAA && <StarFilledIcon />}
              </div>
            </div>
          )}

          <Badge
            className="text-nowrap"
            variant={eventStatusEnumMapping[centerEvent.status].variant}
            text={eventStatusEnumMapping[centerEvent.status].label}
          />
        </div>

        {mode === 'NORMAL' && (
          <div className="flex gap-2 items-center">
            <Button
              variant="ghost"
              label="Imprimer la fiche évènement"
              icon={PrintIcon}
              loading={isPrintPending}
              onClick={() => {
                print()
              }}
            />
            <Button
              variant="ghost"
              label="Clôturer l’évènement"
              icon={CheckMark3Icon}
              onClick={() => toggleModal('resolve')}
            />
          </div>
        )}
      </div>
      <Typography variant="h6" className="text-wrap">
        {centerEvent.types.map((type) => eventTypeEnumMapping[type].label).join(', ')}
      </Typography>
      <div className="flex gap-3 items-center">
        <UserRoleCard
          label="Responsable du centre"
          value={valueRender(centerManager, ({ staff, phoneNumber }) =>
            `${toFullName(staff.firstName, staff.lastName, { options: { mode: 'lastname-firstname' } })} ${phoneNumber ? formatPhoneNumber(phoneNumber) : ''}`.trim()
          )}
          variant="fushia"
          icon={UserSevenIcon}
        />
        <UserRoleCard
          label="FAC Terrain"
          value={valueRender(
            facTerrain ?? undefined,
            (affectedStaff) =>
              `${toFullName(affectedStaff.staff.firstName, affectedStaff.staff.lastName, { options: { mode: 'lastname-firstname' } })}${affectedStaff.facNumber ? ` - ${formatPhoneNumber(affectedStaff.facNumber)}` : ''} - ${DateFormatter.DDMMYYYY(affectedStaff.startDate)} - ${DateFormatter.DDMMYYYY(affectedStaff.endDate)}`
          )}
          variant="blue"
          icon={(props) => <UserFourIcon {...props} light />}
        />
      </div>
      <Modal
        actionButtonsOptions={{
          onConfirm: () => {
            mutateAsync()
          },
          cancelText: 'Fermer',
          confirmText: "Clôturer l'événement"
        }}
        isOpen={modalState.resolve}
        icon={CheckMark3Icon}
        variant={'confirmation'}
        title="Êtes-vous sûr de vouloir clôturer l'événement ?"
        isLoading={isResolvingCenterEventPending}
        onCancel={() => closeModel('resolve')}
      />
    </div>
  )
}

export default CenterEventHeader
